import React, { useState, useEffect } from 'react';

const FlatSelection = ({ formData, updateFormData, nextStep, prevStep }) => {
  const [error, setError] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Add animation classes for staggered appearance
  useEffect(() => {
    const addAnimationClasses = () => {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .stagger-fade-in {
          opacity: 0;
          animation: fadeInScale 0.4s ease-out forwards;
        }
      `;
      document.head.appendChild(style);
      setIsLoaded(true);
    };
    
    addAnimationClasses();
  }, []);
  
  // Organize flat numbers by floor
  const floorData = [
    { name: 'Ground Floor', flats: [1, 2, 3, 4] },
    { name: 'First Floor', flats: [11, 12, 13, 14] },
    { name: 'Second Floor', flats: [21, 22, 23, 24] },
    { name: 'Third Floor', flats: [31, 32, 33, 34] },
  ];
  
  const handleFlatSelect = (flatNumber) => {
    updateFormData({ flatNumber });
    setError('');
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.flatNumber) {
      nextStep();
    } else {
      setError('Please select a flat number before proceeding.');
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl shadow-lg">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-indigo-800 mb-2">Select Your Flat</h2>
        <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-4"></div>
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="inline-flex items-center justify-center bg-indigo-600 text-white px-4 py-2 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v7H7V5zm2 8h2v2H9v-2z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Block {formData.block}</span>
          </div>
          <div className="inline-flex items-center justify-center bg-blue-500 text-white px-4 py-2 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Tower {formData.tower}</span>
          </div>
        </div>
        <p className="text-gray-600 max-w-md mx-auto">
          Please select your flat number from the options below.
        </p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 mb-8">
          {floorData.map((floor, floorIndex) => (
            <div key={floor.name} className="bg-white p-6 rounded-xl shadow-md">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-indigo-800 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 4a1 1 0 011-1h8a1 1 0 011 1v1h1a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7a2 2 0 012-2h1V4zm4 0h2v1H9V4z" clipRule="evenodd" />
                  </svg>
                  {floor.name}
                </h3>
                <div className="w-16 h-0.5 bg-gradient-to-r from-blue-300 to-indigo-400 rounded-full mt-1"></div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {floor.flats.map((flat) => (
                  <button
                    key={flat}
                    type="button"
                    onClick={() => handleFlatSelect(flat)}
                    className={`
                      py-4 flex items-center justify-center font-medium rounded-lg
                      transition-all duration-300 transform
                      ${
                        formData.flatNumber === flat
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg scale-105'
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-400 hover:shadow hover:scale-105'
                      }
                      ${isLoaded ? 'stagger-fade-in' : 'opacity-0'}
                    `}
                    style={{ animationDelay: `${(floorIndex * 0.2) + (flat * 0.03)}s` }}
                  >
                    <span className="text-lg">{flat}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
          
        {error && (
          <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}
        
        <div className="flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            className="bg-white hover:bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-lg border border-gray-300 shadow-sm transition-all duration-300 transform hover:scale-105 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back
          </button>
          
          <button
            type="submit"
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center"
          >
            Next
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
};

export default FlatSelection;
